import StyledWrapper, { getStyledWrapper } from "@insite/client-framework/Common/StyledWrapper";
import { makeHandlerChainAwaitable } from "@insite/client-framework/HandlerCreator";
import logger from "@insite/client-framework/Logger";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import validatePassword from "@insite/client-framework/Store/CommonHandlers/ValidatePassword";
import validateReCaptcha from "@insite/client-framework/Store/Components/ReCaptcha/Handlers/ValidateReCaptcha";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import signIn from "@insite/client-framework/Store/Context/Handlers/SignIn";
import {
    getCountriesDataView,
    getCurrentCountries,
} from "@insite/client-framework/Store/Data/Countries/CountriesSelectors";
import loadCurrentCountries, {
    loadCurrentCountriesParameter,
} from "@insite/client-framework/Store/Data/Countries/Handlers/LoadCurrentCountries";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import { getCreateAccountReturnUrl } from "@insite/client-framework/Store/Pages/CreateAccount/CreateAccountSelectors";
import translate from "@insite/client-framework/Translate";
import { CountryModel, StateModel } from "@insite/client-framework/Types/ApiModels";
import ReCaptcha from "@insite/content-library/Components/ReCaptcha";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import Checkbox from "@insite/mobius/Checkbox";
import CheckboxGroup from "@insite/mobius/CheckboxGroup";
import Clickable, { ClickablePresentationProps } from "@insite/mobius/Clickable";
import { FormFieldStyle } from "@insite/mobius/FormField";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import { IconPresentationProps } from "@insite/mobius/Icon";
import AlertTriangle from "@insite/mobius/Icons/AlertTriangle";
import Eye from "@insite/mobius/Icons/Eye";
import EyeOff from "@insite/mobius/Icons/EyeOff";
import LoadingOverlay from "@insite/mobius/LoadingOverlay";
import { LoadingSpinnerProps } from "@insite/mobius/LoadingSpinner";
import Radio, { RadioComponentProps, RadioStyle } from "@insite/mobius/Radio";
import RadioGroup, { RadioGroupComponentProps } from "@insite/mobius/RadioGroup";
import Select, { SelectPresentationProps, SelectProps } from "@insite/mobius/Select";
import TextField, { InputStyle, TextFieldPresentationProps } from "@insite/mobius/TextField";
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import TypographyStyle from "@insite/mobius/Typography/TypographyStyle";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import FieldSetPresentationProps, { FieldSetGroupPresentationProps } from "@insite/mobius/utilities/fieldSetProps";
import getColor from "@insite/mobius/utilities/getColor";
import { HasHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { formatPhoneNumber } from "@sunSource/Common/Utilities/formatPhoneNumber";
import {
    CallbackType,
    ErrorObjectType,
    InputObjectType,
    validate,
    validateConfirmPasswordHandler,
    validatePasswordHandler,
} from "@sunSource/Common/Utilities/validate";
import {
    FormErrorsStateType,
    intialErrorFormState,
    newCustomerValidatorConfig,
    ValidatorConfigType,
} from "@sunSource/Components/CreateAccountNewFormValidatorConfig";
import setOnePageCreateAccountData from "@sunSource/Handlers/NewOnePageCheckout/OnePageCheckoutCreateAccountHandler";
import updateUserContactInfo from "@sunSource/Handlers/NewOnePageCheckout/OnePageCheckoutUpdateContactInfo";
import addAccount from "@sunSource/Store/Accounts/Handlers/AddAccount";
import CustomApplicationState from "@sunSource/Store/CustomApplicationState";
import SetOnePageTabData from "@sunSource/Store/Data/NewOnePageCheckout/Handlers/OnePageCheckoutTabHandler";
import {
    getOnePageCheckoutAccountState,
    getOnePageCheckoutTabState,
} from "@sunSource/Store/Data/NewOnePageCheckout/OnePageCheckoutStateSelectors";
import React, { ReactNode, RefObject, useContext, useEffect, useState } from "react";
import ReCAPTCHA from "react-google-recaptcha";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

type Props = ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

interface MessageFilterStyles {
    radioGroup?: FieldSetGroupPresentationProps<RadioGroupComponentProps>;
    radioButton?: FieldSetPresentationProps<RadioComponentProps>;
}

const mapStateToProps = (state: CustomApplicationState) => {
    const countriesDataView = getCountriesDataView(state, loadCurrentCountriesParameter);
    return {
        accountSettings: getSettingsCollection(state).accountSettings,
        signInUrl: getPageLinkByPageType(state, "SignInPage")?.url,
        tabState: getOnePageCheckoutTabState(state),
        createAccountData: getOnePageCheckoutAccountState(state),
        userPhoneNumber: state.context.session?.properties?.phoneNumber ? state.context.session?.properties?.phoneNumber : state.context.session?.properties?.phone ? state.context.session?.properties?.phone : getOnePageCheckoutAccountState(state).phoneNumber,
        userJobTitle: state.context.session?.properties?.jobTitle ??  getOnePageCheckoutAccountState(state).jobTitle,
        userCompanyName: state.context.session?.properties?.companyName ??  getOnePageCheckoutAccountState(state).companyName,
        typeofBusiness: state.context.session?.properties?.typeOfBusiness ??  getOnePageCheckoutAccountState(state).businessType,
        isAuthenticated: state.context.session?.isAuthenticated,
        userId: state.context.session?.userProfileId ?? "",
    };
};

const mapDispatchToProps = {
    signIn,
    SetOnePageTabData,
    setOnePageCreateAccountData,
    updateUserContactInfo,
};

export interface CreateAccountNewFormStyles {
    form?: InjectableCss;
    spinner?: LoadingSpinnerProps;
    centeringWrapper?: InjectableCss;
    mainGridContainer?: GridContainerProps;
    fieldsGridItem?: GridItemProps;
    emailGridItem?: GridItemProps;
    emailTextField?: TextFieldPresentationProps;
    firstNameGridItem?: GridItemProps;
    jobTitleGridItem?: GridItemProps;
    companyNameGridItem?: GridItemProps;
    addressLineOneGridItem?: GridItemProps;
    addressLineTwoGridItem?: GridItemProps;
    cityGridItem?: GridItemProps;
    zipCodeGridItem?: GridItemProps;
    lastNameGridItem?: GridItemProps;
    userNameTextField?: TextFieldPresentationProps;
    firstNameTextField?: TextFieldPresentationProps;
    jobTitleTextField?: TextFieldPresentationProps;
    lastNameTextField?: TextFieldPresentationProps;
    companyNameTextField?: TextFieldPresentationProps;
    cityTextField?: TextFieldPresentationProps;
    zipCodeTextField?: TextFieldPresentationProps;
    addressLineOneTextField?: TextFieldPresentationProps;
    addressLineTwoTextField?: TextFieldPresentationProps;
    passwordGridItem?: GridItemProps;
    passwordTextField?: TextFieldPresentationProps;
    passwordIcon?: IconPresentationProps;
    confirmPasswordGridItem?: GridItemProps;
    confirmPasswordTextField?: TextFieldPresentationProps;
    confirmPasswordIcon?: IconPresentationProps;
    stateGridItem?: GridItemProps;
    businessTypeGridItem?: GridItemProps;
    stateTextField?: SelectPresentationProps;
    countryGridItem?: GridItemProps;
    countryTextField?: SelectPresentationProps;
    phoneNumberGridItem?: GridItemProps;
    faxGridItem?: GridItemProps;
    phoneNumberTextField?: TextFieldPresentationProps;
    faxTextField?: TextFieldPresentationProps;
    subscriptionGridItem?: GridItemProps;
    submitErrorGridItem?: GridItemProps;
    submitErrorTitle?: TypographyProps;
    submitGridItem?: GridItemProps;
    reCaptchaGridItem?: GridItemProps;
    submitButton?: ButtonPresentationProps;
    requirementsGridItem?: GridItemProps;
    requirementsTitle?: TypographyProps;
    inputHeader?: InjectableCss;
    passwordRequirementsGridItem?: GridItemProps;
    passwordRequirementsGridContainer?: GridContainerProps;
    passwordGridContainer?: GridContainerProps;
    fieldSetParentDiv?: InjectableCss;
    messageFilter?: MessageFilterStyles;
    requiredFieldDiv?: TypographyProps;
    shipToBillToCheck?: InjectableCss;
    passwordValidationMessage?: InjectableCss;
    loginBtn?: ButtonPresentationProps;
    lostYourPasswordSection?: GridItemProps;
    loginButtonSection?: GridItemProps;
    lostYourPassword?: ClickablePresentationProps;
    companyDetailsWrapper?: InjectableCss;
    companyName?: InjectableCss;
    contactInfo?: InjectableCss;
    changeLabel?: InjectableCss;
    companyDetailsGridItem?: GridItemProps;
}

export const createAccountNewFormStyles: CreateAccountNewFormStyles = {
    requiredFieldDiv: {
        css: css`
            > ${TypographyStyle} {
                margin-top: 15px;
            }
        `,
    },
    centeringWrapper: {
        css: css`
            display: flex;
            height: 300px;
            justify-content: center;
            align-items: center;
        `,
    },
    mainGridContainer: {
        css: css`
            padding: 0px 15px;
            margin-top: 40px;
            margin-bottom: 20px;
            [class^="GridItemStyle"] {
                padding-bottom: 5px;
                margin-bottom: 0;
                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(theme, [
                        css`
                            padding: 0px 12px;
                        `,
                        null,
                        null,
                        null,
                        null,
                    ])}
            }
            [class^="GridOffset"] {
                margin-bottom: 0;
                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(theme, [
                        css`
                            padding: 0px 12px;
                        `,
                        null,
                        null,
                        null,
                        null,
                    ])}
            }
            .containerTitle {
                width: 100%;
                border-bottom: 2px solid ${getColor("primary")};
                background: ${getColor("info.contrast")};
                padding: 10px 0px;
                display: flex;
                margin: 10px 15px;
                color: ${getColor("primary")};
                > ${TypographyStyle} {
                    &:first-child {
                        font-weight: bold;
                    }
                }
                [class^="CheckboxGroupStyle"] {
                    margin-left: 30px;
                }
            }
            [class^="FormInputWrapper"] {
                margin-top: 0 !important;
                > ${InputStyle} {
                    :focus {
                        border: 2px solid ${getColor("primary")} !important;
                    }
                }
                > select[class^="SelectStyle"] {
                    :focus {
                        border: 2px solid ${getColor("primary")} !important;
                    }
                }
            }
        `,
    },
    businessTypeGridItem: {
        width: 12,
        css: css`
            flex-direction: column;
            > div[class^="RadioGroupStyle"] {
                justify-content: flex-start;
                display: inline-block;
                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(
                        theme,
                        [
                            null,
                            null,
                            css`
                                width: 100%;
                            `,
                            null,
                            null,
                        ],
                        "max",
                    )}
                > div[class^="RadioStyle"] {
                    margin-top: 0;
                    margin-right: 25px;
                    display: inline-block;
                    ${({ theme }: { theme: BaseTheme }) =>
                        breakpointMediaQueries(theme, [
                            css`
                                width: 50%;
                                margin-right: 0;
                            `,
                            css`
                                width: 50%;
                                margin-right: 0;
                            `,
                            null,
                            null,
                            null,
                        ])}
                    &:last-child {
                        margin-right: 0;
                    }
                    > label[class^="TypographyStyle"] {
                        font-size: 13px;
                    }
                }
            }
        `,
    },
    fieldsGridItem: {
        width: [12, 12, 12, 6, 6],
    },
    countryGridItem: {
        width: [12, 12, 12, 6, 6],
        css: css`
            display: none;
            > ${FormFieldStyle} {
                flex-direction: column;
                > ${TypographyStyle} {
                    text-align: left !important;
                    width: 100% !important;
                    padding: 0;
                    + div[class^="FormInputWrapper"] {
                        margin-top: 10px;
                        width: 100%;
                        > select {
                            height: 39.98px;
                        }
                    }
                }
            }
        `,
    },
    stateGridItem: {
        width: [12, 12, 12, 6, 6],
        css: css`
            > ${FormFieldStyle} {
                flex-direction: column;
                > ${TypographyStyle} {
                    text-align: left !important;
                    width: 100% !important;
                    padding: 0;
                    + div[class^="FormInputWrapper"] {
                        margin-top: 10px;
                        width: 100%;
                        > select {
                            height: 39.98px;
                        }
                    }
                }
            }
        `,
    },
    subscriptionGridItem: {
        width: 12,
        css: css`
            flex-wrap: wrap;
            padding: 0px 0px 0px 15px;
        `,
    },
    submitErrorGridItem: {
        width: [12, 12, 12, 6, 6],
    },
    submitErrorTitle: {
        color: "danger",
    },
    reCaptchaGridItem: { width: 12 },
    submitGridItem: {
        width: 12,
        css: css`
            justify-content: end;
            padding: 0px 0px 35px 0px;
        `,
    },
    emailTextField: {
        cssOverrides: {
            inputSelect: css`
                font-size: 16px;
                padding: 0 2rem 0 10px !important;
                &:focus {
                    padding: 0 2rem 0 10px !important;
                }
            `,
        },
    },
    fieldSetParentDiv: {
        css: css`
            display: inline-block;
            vertical-align: top;
            legend{
                display: none;
            }
            label{
                font-size: 14px !important;
            }
            > ${RadioStyle} {
              
             margin-top:5px !important;
                white-space: nowrap;
            }
        `,
    },
    shipToBillToCheck: {
        css: css`
            margin-top: 15px;
            margin-left: 15px;
            width: 100%;
        `,
    },
    inputHeader: {
        css:css`
        color: dimgray;
        margin-bottom: 5px;
        margin-top: 10px;
        display: block;
        font-size: 18px !important;
        `
    },
    passwordValidationMessage: {
        css: css`
            ul {
                list-style: initial;
                padding-left: 20px;
                font-size: 14px;
            }
        `,
    },

    loginBtn: {
        variant: "tertiary",
        css: css`
            width: 120px;
            text-transform: uppercase;
            > ${TypographyStyle} {
                font-weight: 400;
            }
        `,
    },
    lostYourPasswordSection: {
        width: [12, 12, 6, 6, 6],
        css: css``,
    },
    lostYourPassword: {
        css: css`
            color: ${getColor("primary")};
        `,
    },
    loginButtonSection: {
        width: [12, 12, 12, 12, 12],
        css: css`
            justify-content: right;
            gap: 15px;
        `,
    },
    companyDetailsWrapper: {
        css: css`
            display: flex;
            flex-direction: column;
        `,
    },
    companyName: {
        css: css`
            font-weight: 600;
        `,
    },
    contactInfo: {
        css: css`
            color: gray;
        `,
    },
    changeLabel: {
        css: css`
            color: #00539b;
            font-weight: 600;
            cursor: pointer;
        `,
    },
    companyDetailsGridItem: {
        width: 6,
        css: css`
            padding-top: 0px;
        `,
    },
};

const styles = createAccountNewFormStyles;

const StyledForm = getStyledWrapper("form");

const ContactAndCompanyInfo = ({
    accountSettings,
    signInUrl,
    createAccountData,
    signIn,
    tabState,
    SetOnePageTabData,
    setOnePageCreateAccountData,
    userPhoneNumber,
    userJobTitle,
    userCompanyName,
    typeofBusiness,
    isAuthenticated,
    userId,
    updateUserContactInfo,
}: Props) => {
    const companyNameRequiredFieldMessage = siteMessage("Company name is Required");
    const phoneNumberRequiredFieldMessage = siteMessage("Phone number is Required");
    const jobTitleRequiredFieldMessage = siteMessage("Job Title is Required");

    const [formErrors, setFormErrors] = React.useState<FormErrorsStateType>(intialErrorFormState);
    const [jobTitle, setJobTitle] = useState(userJobTitle);
    const [companyName, setCompanyName] = useState(userCompanyName);
    const [phoneNumber, setPhoneNumber] = useState(userPhoneNumber);
    const [changeClicked, setChangeClicked] = useState(false);

    const [errorMessage, setErrorMessage] = useState("");
    const [isSubmitted, setIsSubmitted] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isFormSubmitted, setFormSubmitted] = React.useState(false);
    const toasterContext = useContext(ToasterContext);
    const recaptchaRef: RefObject<ReCAPTCHA> = React.createRef();
    const [isButtonDisabled, setIsButtonDisabled] = React.useState(true);

    const [businessType, setBusinessType] = React.useState(typeofBusiness ? typeofBusiness :"OEM (Original Equipment Manufacturer)");

    const [jobTitleError, setJobTitleError] = useState(
        accountSettings?.useEmailAsUserName ? "" : jobTitleRequiredFieldMessage,
    );

    const [companyNameError, setCompanyNameError] = useState(
        accountSettings?.useEmailAsUserName ? "" : companyNameRequiredFieldMessage,
    );

    const [phoneNumberError, setPhoneNumberError] = useState(
        accountSettings?.useEmailAsUserName ? "" : phoneNumberRequiredFieldMessage,
    );

    React.useEffect(() => {
        setIsButtonDisabled(validateSubmitDisabled(formErrors));
    }, [formErrors]);

    React.useEffect(() => {
        const cloneFormErrors = { ...formErrors };
        for (const [key] of Object.entries(cloneFormErrors)) {
            const element: HTMLSelectElement | HTMLInputElement | null = document.querySelector(`[name="${key}"]`);
            if (element && element.value) {
                delete cloneFormErrors[key as keyof FormErrorsStateType];
                setFormErrors(removeKey(cloneFormErrors, key));
            }
        }
    }, []);

    const resetForm = () => {
        setJobTitle("");
        setJobTitleError("");
        setCompanyName("");
        setCompanyNameError("");
        setPhoneNumber("");
        setPhoneNumberError("");
    };

    const validateSubmitDisabled = (errorsState: FormErrorsStateType) => {
        return Object.keys(errorsState).length !== 0;
    };

    const removeKey = (formErrorObject: FormErrorsStateType, deleteKey: string) => {
        return Object.assign(
            {},
            ...Object.entries(formErrorObject)
                .filter(([k]) => k !== deleteKey)
                .map(([k, v]) => ({ [k]: v })),
        );
    };

    const validateHandler = (input: HTMLInputElement | HTMLSelectElement, customValidation?: CallbackType): void => {
        const inputObject: InputObjectType = {
            name: input.name,
            value: input.value,
        };
        const validatorConfig = newCustomerValidatorConfig[input.name as keyof ValidatorConfigType];
        const isValidate: ErrorObjectType | undefined = validate(inputObject, validatorConfig, customValidation);

        if (isValidate === undefined) {
            delete formErrors[input.name as keyof FormErrorsStateType];
            setFormErrors(formErrors);
        } else {
            setFormErrors({
                ...formErrors,
                [input.name]: isValidate ? isValidate.errorMsg : isValidate,
            });
        }
        if (!validatorConfig.required && input.value === "") {
            delete formErrors[input.name as keyof FormErrorsStateType];
            setFormErrors(formErrors);
        }

        setIsButtonDisabled(validateSubmitDisabled(formErrors));
    };

    const validateSubmitEnabled = () => {
        return !(jobTitleError || companyNameError || phoneNumberError);
    };

    const jobTitleChangeHandler = (input: HTMLInputElement) => {
        validateHandler(input);
        setJobTitle(input.value);
        setOnePageCreateAccountData({jobTitle:input.value});
    };
    const companyNameChangeHandler = (input: HTMLInputElement) => {
        validateHandler(input);
        setCompanyName(input.value);
        setOnePageCreateAccountData({companyName:input.value})
    };

    const phoneNumberChangeHandler = (input: HTMLInputElement) => {
        validateHandler(input);
        setPhoneNumber(formatPhoneNumber(input.value));
        setOnePageCreateAccountData({phoneNumber:input.value})
    };

    const businessTypeChangeHandler = (businessType: string) => {
        setBusinessType(businessType);
        setOnePageCreateAccountData({businessType:businessType})
    };

    const onSuccessHandler = () => {
        setErrorMessage("");
        setIsButtonDisabled(true);
        toasterContext.addToast({
            body: siteMessage("Your account has been successfully created"),
            messageType: "success",
            timeoutLength: 2500,
        });
        function getDataLayer() {
            return (window as any).dataLayer;
        }
        const dataLayer = getDataLayer();
        if (dataLayer) {
            dataLayer.push({
                event: "sign_up",
                sign_up_type: "new",
            });
        }

        // setTimeout(() => {
        //     signIn({
        //         userName: email,
        //         password,
        //         rememberMe: false,
        //         returnUrl,
        //     });
        // }, 500);
    };

    const submitHandler = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        setIsButtonDisabled(true);

        const isReCaptchaValid = await validateReCaptcha({ location: "CreateAccount", recaptchaRef });
        if (!isReCaptchaValid) {
            setIsButtonDisabled(false);
            return;
        }

        if (!isSubmitted) {
            setIsSubmitted(true);
        }
    };

    const componentStyles: MessageFilterStyles = styles.messageFilter || {};

    const loginHandler = () => {
        setOnePageCreateAccountData({ phoneNumber, jobTitle, companyName, businessType });
        completeAction(1);
    };

    const updateHandler = () => {
        if (jobTitle && phoneNumber && companyName && businessType) {
        updateUserContactInfo({
            PhoneNumber: phoneNumber,
            JobTitle: jobTitle,
            CompanyName: companyName,
            TypeOfBusiness: businessType,
            UserId: userId,
            onComplete(result) {
                setCompanyName(companyName);
                setPhoneNumber(phoneNumber);
                setJobTitle(jobTitle);
                setBusinessType(businessType);
                setChangeClicked(false);
                // toasterContext.addToast({
                //     body: siteMessage("Contact info & company details updated."),
                //     messageType: "success",
                //     timeoutLength: 2500,
                // });
            },
        });
        completeAction(1);
    } else {
        toasterContext.addToast({
            body: siteMessage("Phone Number, Job Title, Company Name & Type of Business is required."),
            messageType: "danger",
            timeoutLength: 2500,
        });
    }
    };

    const cancelHandler = ()=>{
        setCompanyName(userCompanyName);
        setPhoneNumber(userPhoneNumber);
        setJobTitle(userJobTitle);
        setBusinessType(typeofBusiness);
        setJobTitleError("");
        setCompanyNameError("");
        setPhoneNumberError("");
        setChangeClicked(false);
        setFormErrors({
            ...formErrors,
            companyName:"",
            phoneNumber:"",
            jobTitle:""
        });
    }

    const [openTabs, setOpenTabs] = useState<number[]>(tabState.openTabs);
    const [completedTabs, setCompletedTabs] = useState<number[]>(tabState.completedTabs);
    const [currentTab, setCurrentTab] = useState<number>(tabState.currentTab);

    const completeAction = (index: number) => {
        setCurrentTab(index + 1);
        if (!completedTabs.includes(index)) {
            setCompletedTabs([...completedTabs, index]);
            setOpenTabs(prev => [...prev, index + 1]);
        }
    };

    const isInContentAdmin = () => {
            try {
              const parentUrl = window?.parent?.location?.toString().toLowerCase();
              return parentUrl.includes("/contentadmin/");
            } catch (error) {
              // Catch cross-origin errors (if parent URL can't be accessed)
              return false;
            }
          };
    React.useEffect(()=>{
        if(isInContentAdmin()){
            completeAction(1);
        }
    },[])
    React.useEffect(() => {
        SetOnePageTabData({
            openTabs,
            completedTabs,
            currentTab,
        });
    }, [openTabs, completedTabs, currentTab, SetOnePageTabData]);

    useEffect(() => {
        if (jobTitle && phoneNumber && companyName && businessType) {
            setOnePageCreateAccountData({ phoneNumber, jobTitle, companyName, businessType });
            completeAction(1);
        }
    }, [jobTitle, phoneNumber, companyName, businessType]);

    useEffect(() => {
        // if (jobTitle && phoneNumber && companyName && businessType) {
        if (isAuthenticated) {
            setOnePageCreateAccountData({ phoneNumber, jobTitle, companyName, businessType });
            completeAction(1);
        }
        // }, [jobTitle, phoneNumber, companyName, businessType]);
    }, [isAuthenticated]);

    if (isAuthenticated && !changeClicked) {
        return (
            <StyledWrapper>
                <GridContainer {...styles.mainGridContainer}>
                    <GridItem {...styles.companyDetailsGridItem}>
                        <StyledWrapper {...styles.companyDetailsWrapper}>
                            <Typography {...styles.companyName}>
                                {companyName}{" "}
                                <Typography
                                    onClick={() => {
                                        setChangeClicked(true);
                                    }}
                                    {...styles.changeLabel}
                                >
                                    {translate("(Change)")}
                                </Typography>
                            </Typography>
                            <Typography {...styles.contactInfo}>{phoneNumber}</Typography>
                            <Typography {...styles.contactInfo}>{businessType}</Typography>
                        </StyledWrapper>
                    </GridItem>
                </GridContainer>
            </StyledWrapper>
        );
    }
    return (
        <StyledWrapper>
            <LoadingOverlay loading={isSubmitted} spinnerProps={{ color: "primary" }}>
                <GridContainer {...styles.mainGridContainer}>
                    <GridContainer className="personalInfoDiv">
                        <GridItem {...styles.fieldsGridItem}>
                            <TextField
                                {...styles.phoneNumberTextField}
                                name="phoneNumber"
                                value={phoneNumber}
                                data-test-selector="createAccount_phoneNumber"
                                onChange={e => phoneNumberChangeHandler(e.currentTarget)}
                                onBlur={e => validateHandler(e.currentTarget)}
                                error={!!formErrors.phoneNumber && formErrors.phoneNumber}
                                iconProps={{
                                    color: "danger",
                                    src: formErrors.phoneNumber ? AlertTriangle : undefined,
                                }}
                                placeholder="Phone Number *"
                                required
                            />
                        </GridItem>
                        <GridItem {...styles.fieldsGridItem}>
                            <TextField
                                {...styles.jobTitleTextField}
                                name="jobTitle"
                                value={jobTitle}
                                data-test-selector="createAccount_jobTitle"
                                onChange={e => jobTitleChangeHandler(e.currentTarget)}
                                onBlur={e => validateHandler(e.currentTarget)}
                                error={!!formErrors.jobTitle && formErrors.jobTitle}
                                iconProps={{
                                    color: "danger",
                                    src: formErrors.jobTitle ? AlertTriangle : undefined,
                                }}
                                placeholder="Job Title *"
                                required
                            />
                        </GridItem>
                        <GridItem {...styles.fieldsGridItem} width={12}>
                            <TextField
                                {...styles.companyNameTextField}
                                name="companyName"
                                value={companyName}
                                data-test-selector="createAccount_companyName"
                                onChange={e => companyNameChangeHandler(e.currentTarget)}
                                onBlur={e => validateHandler(e.currentTarget)}
                                error={!!formErrors.companyName && formErrors.companyName}
                                iconProps={{
                                    color: "danger",
                                    src: formErrors.companyName ? AlertTriangle : undefined,
                                }}
                                placeholder="Company Name *"
                                required
                            />
                        </GridItem>

                        <GridItem {...styles.businessTypeGridItem}>
                              <Typography {...styles.inputHeader}>{translate("Type of Business *")}</Typography>
                            <RadioGroup
                                {...styles.fieldSetParentDiv}
                                value={businessType}
                                onChangeHandler={e => {
                                    businessTypeChangeHandler(e.target.value);
                                }}
                            >
                                <Radio
                                    {...componentStyles.radioButton}
                                    key="OEM (Original Equipment Manufacturer)"
                                    value="OEM (Original Equipment Manufacturer)"
                                >
                                    {translate("OEM (Original Equipment Manufacturer)")}
                                </Radio>
                                <Radio
                                    {...componentStyles.radioButton}
                                    key="MRO (Maintenance, Repair & Operations)"
                                    value="MRO (Maintenance, Repair & Operations)"
                                >
                                    {translate("MRO (Maintenance, Repair & Operations)")}
                                </Radio>
                                <Radio {...componentStyles.radioButton} key="Reseller" value="Reseller">
                                    {translate("Reseller")}
                                </Radio>
                                <Radio {...componentStyles.radioButton} key="Other" value="Other">
                                    {translate("Other")}
                                </Radio>
                            </RadioGroup>
                        </GridItem>

                        {isAuthenticated && changeClicked && (
                            <GridItem {...styles.loginButtonSection}>
                                <Button
                                    {...styles.loginBtn}
                                    onClick={updateHandler}
                                >
                                    {translate("Save")}
                                </Button>
                            </GridItem>
                        )}
                    </GridContainer>
                </GridContainer>
            </LoadingOverlay>
        </StyledWrapper>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(ContactAndCompanyInfo);
